import { useState, useEffect, useMemo } from 'react'
import serviceTypeAlternativeNamesService from '../services/serviceTypeAlternativeNamesService'

/**
 * Service Type Alternative Names — Enterprise Standard
 * Lets admins maintain commissioner-language alternative names for each
 * canonical service type in the taxonomy. Saved values are heavily cached
 * server-side and picked up by tender ingestion/matching immediately after
 * a save (no deploy needed).
 */
export default function ServiceTypeAlternativeNames() {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [search, setSearch] = useState('')
    const [expanded, setExpanded] = useState(() => new Set())
    const [showAddForm, setShowAddForm] = useState(false)
    const [deletingCategory, setDeletingCategory] = useState(null)
    const [statusFilter, setStatusFilter] = useState('all')
    const [togglingCategory, setTogglingCategory] = useState(null)

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        setError(null)
        try {
            const data = await serviceTypeAlternativeNamesService.getAll()
            setRows(data)
        } catch (err) {
            setError(err.message || 'Failed to load service types')
        } finally {
            setLoading(false)
        }
    }

    const grouped = useMemo(() => {
        const term = search.trim().toLowerCase()
        const byStatus = rows.filter(r => statusFilter === 'all' || (statusFilter === 'active' ? r.isActive : !r.isActive))
        const filtered = term
            ? byStatus.filter(r =>
                r.serviceType.toLowerCase().includes(term) ||
                r.category.toLowerCase().includes(term) ||
                r.alternativeNames.some(n => n.toLowerCase().includes(term)) ||
                (r.legacyNames || []).some(n => n.toLowerCase().includes(term))
            )
            : byStatus

        const map = new Map()
        for (const row of filtered) {
            if (!map.has(row.category)) map.set(row.category, [])
            map.get(row.category).push(row)
        }
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
    }, [rows, search, statusFilter])

    const toggleCategory = (category) => {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(category)) next.delete(category)
            else next.add(category)
            return next
        })
    }

    const handleRowSaved = (updated) => {
        setRows(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)))
    }

    const handleCreated = (created) => {
        setRows(prev => [...prev, created])
        setShowAddForm(false)
        setExpanded(prev => new Set(prev).add(created.category))
    }

    const handleDeleted = (id) => {
        setRows(prev => prev.filter(r => r.id !== id))
    }

    const handleToggleCategory = async (category, isActive) => {
        const verb = isActive ? 'Reactivate' : 'Deactivate'
        if (!window.confirm(isActive
            ? `Reactivate every service type in "${category}"? They will be offered to organisations again.`
            : `Deactivate every service type in "${category}"? They will no longer be offered to organisations, but existing selections and tender classifications are kept.`)) return
        setTogglingCategory(category)
        setError(null)
        try {
            await serviceTypeAlternativeNamesService.setCategoryActive(category, isActive)
            setRows(prev => prev.map(r => (r.category === category ? { ...r, isActive } : r)))
        } catch (err) {
            setError(err.message || `${verb} failed`)
        } finally {
            setTogglingCategory(null)
        }
    }

    const handleDeleteCategory = async (category, count) => {
        if (!window.confirm(`Permanently delete the "${category}" category and all ${count} service type${count === 1 ? '' : 's'} in it? This is only allowed if nothing uses them, and cannot be undone. To stop offering them, use Deactivate instead.`)) return
        setDeletingCategory(category)
        setError(null)
        try {
            await serviceTypeAlternativeNamesService.removeCategory(category)
            setRows(prev => prev.filter(r => r.category !== category))
        } catch (err) {
            setError(err.message || 'Failed to delete category')
        } finally {
            setDeletingCategory(null)
        }
    }

    const categoryOptions = useMemo(
        () => Array.from(new Set(rows.map(r => r.category))).sort((a, b) => a.localeCompare(b)),
        [rows]
    )

    const isSearching = search.trim().length > 0

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Service Type Alternative Names</h1>
                    <p className="text-sm text-admin-muted mt-1 max-w-2xl">
                        Add, edit or deactivate service types and the different wordings commissioners use for each
                        (e.g. "Domiciliary support" for "Home Care / Domiciliary Care"). Saved changes are cached
                        and applied to tender matching immediately. Deactivated types are no longer offered to organisations
                        but existing selections and tender classifications are kept; renaming keeps the old name as a Legacy Name.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setShowAddForm(v => !v)} className="btn-primary text-xs whitespace-nowrap">
                        {showAddForm ? 'Cancel' : 'New Service Type'}
                    </button>
                    <button onClick={load} disabled={loading} className="btn-secondary text-xs whitespace-nowrap">
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <datalist id="service-type-category-options">
                {categoryOptions.map((c) => <option key={c} value={c} />)}
            </datalist>

            {showAddForm && (
                <AddServiceTypeForm
                    onCreated={handleCreated}
                    onCancel={() => setShowAddForm(false)}
                />
            )}

            <div className="flex flex-wrap gap-3 max-w-2xl">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search service type, category, or alternative name..."
                    className="input-admin flex-1 min-w-[16rem]"
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-admin" aria-label="Filter by status">
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {error && (
                <div className="p-4 bg-status-error-bg border border-status-error/20 rounded-lg text-sm text-status-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="card-admin p-8 text-center text-sm text-admin-muted">Loading service types...</div>
            ) : grouped.length === 0 ? (
                <div className="card-admin p-8 text-center text-sm text-admin-muted">No matching service types.</div>
            ) : (
                <div className="space-y-3">
                    {grouped.map(([category, categoryRows]) => {
                        const open = isSearching || expanded.has(category)
                        return (
                            <div key={category} className="card-admin overflow-hidden">
                                <div className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between gap-4 hover:bg-gray-100 transition-colors">
                                    <button
                                        onClick={() => toggleCategory(category)}
                                        className="flex-1 flex items-center justify-between gap-4 text-left"
                                    >
                                        <div>
                                            <h2 className="text-sm font-bold text-admin-heading">{category}</h2>
                                            <p className="text-xs text-admin-muted mt-0.5">{categoryRows.length} service type{categoryRows.length === 1 ? '' : 's'}</p>
                                        </div>
                                        <svg
                                            className={`w-4 h-4 text-admin-muted transition-transform ${open ? 'rotate-180' : ''}`}
                                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {categoryRows.every(r => !r.isActive) && (
                                        <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-200 text-slate-600">Inactive</span>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleToggleCategory(category, categoryRows.every(r => !r.isActive)) }}
                                        disabled={togglingCategory === category}
                                        className="flex-shrink-0 btn-secondary text-xs px-3 py-1.5 disabled:opacity-40"
                                    >
                                        {togglingCategory === category ? 'Updating...' : (categoryRows.every(r => !r.isActive) ? 'Reactivate Category' : 'Deactivate Category')}
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category, categoryRows.length) }}
                                        disabled={deletingCategory === category}
                                        title="Only possible when nothing uses this category. Otherwise deactivate it."
                                        className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border border-status-error/30 text-status-error hover:bg-status-error-bg disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {deletingCategory === category ? 'Deleting...' : 'Delete (unused only)'}
                                    </button>
                                </div>
                                {open && (
                                    <div className="divide-y divide-admin-border">
                                        {categoryRows.map((row) => (
                                            <ServiceTypeRow key={row.id} row={row} onSaved={handleRowSaved} onDeleted={handleDeleted} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function AddServiceTypeForm({ onCreated, onCancel }) {
    const [serviceType, setServiceType] = useState('')
    const [category, setCategory] = useState('')
    const [names, setNames] = useState([])
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    const addName = () => {
        const value = draft.trim()
        if (!value) return
        if (!names.some(n => n.toLowerCase() === value.toLowerCase())) {
            setNames(prev => [...prev, value])
        }
        setDraft('')
    }

    const removeName = (name) => {
        setNames(prev => prev.filter(n => n !== name))
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            addName()
        }
    }

    const handleCreate = async () => {
        setError(null)
        if (!serviceType.trim() || !category.trim()) {
            setError('Service type and category are both required.')
            return
        }
        setSaving(true)
        try {
            const created = await serviceTypeAlternativeNamesService.create(serviceType.trim(), category.trim(), names)
            onCreated(created)
        } catch (err) {
            setError(err.message || 'Failed to create service type')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="card-admin p-6 space-y-4">
            <h2 className="text-sm font-bold text-admin-heading">New Service Type</h2>

            {error && (
                <div className="p-3 bg-status-error-bg border border-status-error/20 rounded-lg text-sm text-status-error">
                    {error}
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-admin-muted mb-1">Service type name</label>
                    <input
                        type="text"
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value)}
                        placeholder="e.g. Supported Living"
                        className="input-admin w-full text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-admin-muted mb-1">Category</label>
                    <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="e.g. Adult Social Care"
                        list="service-type-category-options"
                        className="input-admin w-full text-sm"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-medium text-admin-muted mb-1">Alternative names / keywords (optional)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                    {names.map((name) => (
                        <span
                            key={name}
                            className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium bg-brand-navy/5 text-brand-navy border border-brand-navy/10"
                        >
                            {name}
                            <button
                                onClick={() => removeName(name)}
                                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-brand-navy/10 text-brand-navy/60 hover:text-brand-navy"
                                aria-label={`Remove ${name}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2 max-w-md">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Add an alternative name..."
                        className="input-admin flex-1 text-sm py-1.5"
                    />
                    <button onClick={addName} disabled={!draft.trim()} className="btn-secondary text-xs px-3 disabled:opacity-40">
                        Add
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button onClick={handleCreate} disabled={saving} className="btn-primary text-xs px-4 py-2 disabled:opacity-40">
                    {saving ? 'Creating...' : 'Create Service Type'}
                </button>
                <button onClick={onCancel} className="btn-secondary text-xs px-4 py-2">
                    Cancel
                </button>
            </div>
        </div>
    )
}

function ServiceTypeRow({ row, onSaved, onDeleted }) {
    const [names, setNames] = useState(row.alternativeNames)
    const [serviceType, setServiceType] = useState(row.serviceType)
    const [category, setCategory] = useState(row.category)
    const [editingDetails, setEditingDetails] = useState(false)
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [toggling, setToggling] = useState(false)
    const [status, setStatus] = useState(null)

    useEffect(() => {
        setNames(row.alternativeNames)
        setServiceType(row.serviceType)
        setCategory(row.category)
    }, [row.alternativeNames, row.serviceType, row.category])

    const dirty = useMemo(() => {
        if (serviceType.trim() !== row.serviceType) return true
        if (category.trim() !== row.category) return true
        if (names.length !== row.alternativeNames.length) return true
        const original = new Set(row.alternativeNames)
        return names.some(n => !original.has(n))
    }, [names, serviceType, category, row.alternativeNames, row.serviceType, row.category])

    const addName = () => {
        const value = draft.trim()
        if (!value) return
        if (!names.some(n => n.toLowerCase() === value.toLowerCase())) {
            setNames(prev => [...prev, value])
        }
        setDraft('')
    }

    const removeName = (name) => {
        setNames(prev => prev.filter(n => n !== name))
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            addName()
        }
    }

    const handleSave = async () => {
        setSaving(true)
        setStatus(null)
        try {
            const updated = await serviceTypeAlternativeNamesService.update(row.id, {
                serviceType: serviceType.trim(),
                category: category.trim(),
                alternativeNames: names,
            })
            onSaved(updated)
            setEditingDetails(false)
            setStatus('saved')
            setTimeout(() => setStatus(null), 2500)
        } catch (err) {
            setStatus(err.message || 'error')
        } finally {
            setSaving(false)
        }
    }

    const cancelDetailsEdit = () => {
        setServiceType(row.serviceType)
        setCategory(row.category)
        setEditingDetails(false)
    }

    const handleToggleActive = async () => {
        const next = !row.isActive
        if (!window.confirm(next
            ? `Reactivate "${row.serviceType}"? It will be offered to organisations again.`
            : `Deactivate "${row.serviceType}"? It will no longer be offered to organisations. Existing selections and tender classifications are kept.`)) return
        setToggling(true)
        setStatus(null)
        try {
            const updated = await serviceTypeAlternativeNamesService.setActive(row.id, next)
            onSaved(updated)
        } catch (err) {
            setStatus(err.message || 'error')
        } finally {
            setToggling(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm(`Permanently delete "${row.serviceType}"? This is only allowed if nothing uses it, and cannot be undone. To stop offering it, use Deactivate instead.`)) return
        setDeleting(true)
        setStatus(null)
        try {
            await serviceTypeAlternativeNamesService.remove(row.id)
            onDeleted(row.id)
        } catch (err) {
            setStatus(err.message || 'error')
            setDeleting(false)
        }
    }

    return (
        <div className="px-6 py-4">
            <div className="flex items-start justify-between gap-4">
                {editingDetails ? (
                    <div className="flex-1 grid sm:grid-cols-2 gap-3 max-w-xl">
                        <div>
                            <label className="block text-xs font-medium text-admin-muted mb-1">Service type name</label>
                            <input
                                type="text"
                                value={serviceType}
                                onChange={(e) => setServiceType(e.target.value)}
                                className="input-admin w-full text-sm py-1.5"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-admin-muted mb-1">Category</label>
                            <input
                                type="text"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                list="service-type-category-options"
                                className="input-admin w-full text-sm py-1.5"
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className={`text-sm font-semibold ${row.isActive ? 'text-admin-heading' : 'text-admin-muted'}`}>
                            {row.serviceType}
                            <span className={`ml-2 align-middle text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${row.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                                {row.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </p>
                        <p className="text-xs text-admin-muted mt-0.5">{row.category}</p>
                        {(row.legacyNames || []).length > 0 && (
                            <p className="text-xs text-admin-muted mt-0.5">Legacy names: {row.legacyNames.join(', ')}</p>
                        )}
                    </div>
                )}
                <div className="flex items-center gap-3 flex-shrink-0">
                    {status === 'saved' && <span className="text-xs text-status-success font-medium">Saved</span>}
                    {status && status !== 'saved' && <span className="text-xs text-status-error font-medium">{status}</span>}
                    {editingDetails ? (
                        <button onClick={cancelDetailsEdit} disabled={saving} className="btn-secondary text-xs px-3 py-1.5">
                            Cancel
                        </button>
                    ) : (
                        <button onClick={() => setEditingDetails(true)} className="btn-secondary text-xs px-3 py-1.5">
                            Edit
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={!dirty || saving || deleting}
                        className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={handleToggleActive}
                        disabled={toggling || saving || deleting}
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40"
                    >
                        {toggling ? 'Updating...' : (row.isActive ? 'Deactivate' : 'Reactivate')}
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={deleting || saving || toggling}
                        title="Only possible when nothing uses this service type. Otherwise deactivate it."
                        className="text-xs px-3 py-1.5 rounded-lg border border-status-error/30 text-status-error hover:bg-status-error-bg disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {deleting ? 'Deleting...' : 'Delete (unused only)'}
                    </button>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
                {names.map((name) => (
                    <span
                        key={name}
                        className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium bg-brand-navy/5 text-brand-navy border border-brand-navy/10"
                    >
                        {name}
                        <button
                            onClick={() => removeName(name)}
                            className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-brand-navy/10 text-brand-navy/60 hover:text-brand-navy"
                            aria-label={`Remove ${name}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {names.length === 0 && (
                    <span className="text-xs text-admin-muted italic">No alternative names yet.</span>
                )}
            </div>

            <div className="mt-3 flex gap-2 max-w-md">
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add an alternative name..."
                    className="input-admin flex-1 text-sm py-1.5"
                />
                <button onClick={addName} disabled={!draft.trim()} className="btn-secondary text-xs px-3 disabled:opacity-40">
                    Add
                </button>
            </div>
        </div>
    )
}
